import type { Patch } from "@freightsimple/shared/types";
import { Dropdown } from "@freightsimple/ui-core/Controls/Dropdown";
import { Stack } from "@freightsimple/ui-core/ComponentLayout/Stack";
import type { ErrorMessageType } from "@freightsimple/ui-core/FormLayout/FormItem";

import CreditCardsBillingAddressInput from "@/Shared/CreditCards/Inputs/CreditCardsBillingAddressInput";
import type { Address } from "@freightsimple/generated-dashboard-openapi-client/models/Address";
import { useOnce } from "@/Hooks/useOnce";
import { useState } from "react";

interface CardAddressProps {
  addresses: Array<Address>;

  address: Patch<Address>;
  onAddressChanged: (_: Patch<Address>) => void;
  onResetAddress: () => void;

  validated: boolean;

  forceValidation: boolean;
  errorMessageForAddress: ErrorMessageType;
  errorMessageForCity: ErrorMessageType;
  errorMessageForStateOrProvinceCode: ErrorMessageType;
  locationsErrorMessageForPostalCode: ErrorMessageType;
  errorMessageForCountry: ErrorMessageType;
}

const CUSTOM_ADDRESS_VALUE = "__custom_address__";

const CreditCardsCardAddress: React.FC<CardAddressProps> = (
  props: CardAddressProps,
) => {
  const [showCustomAddressForm, setShowCustomAddressForm] = useState(
    props.addresses.length === 0,
  );
  const [selectedIndex, setSelectedIndex] = useState(0);

  const options = [
    ...props.addresses.map((a, idx) => ({
      value: String(idx),
      label: [a.addressLine, a.city, a.postalCode]
        .filter((i) => i !== "")
        .join(", "),
    })),
    {
      value: CUSTOM_ADDRESS_VALUE,
      label: "Enter a different address...",
    },
  ];

  function handleSelect(value: string) {
    if (value === CUSTOM_ADDRESS_VALUE) {
      setSelectedIndex(props.addresses.length);
      setShowCustomAddressForm(true);
      props.onResetAddress();
      return;
    }
    const idx = Number(value);
    const address = props.addresses[idx];
    if (!address) {
      throw new Error("Missing item");
    }
    setSelectedIndex(idx);
    setShowCustomAddressForm(false);
    props.onAddressChanged(address);
  }

  useOnce(function () {
    if (props.addresses.length > 0) {
      handleSelect(String(selectedIndex));
    }
  });

  const selectedValue =
    selectedIndex === props.addresses.length
      ? CUSTOM_ADDRESS_VALUE
      : String(selectedIndex);

  return (
    <Stack gap={4}>
      {props.addresses.length > 0 && (
        <Dropdown
          data-testid="billing-saved-address"
          value={selectedValue}
          options={options}
          onChange={handleSelect}
        />
      )}
      {showCustomAddressForm && (
        <CreditCardsBillingAddressInput
          enabled={true}
          address={props.address}
          addressChanged={props.onAddressChanged}
          forceValidation={props.forceValidation}
          errorMessageForAddress={props.errorMessageForAddress}
          errorMessageForCity={props.errorMessageForCity}
          errorMessageForStateOrProvinceCode={
            props.errorMessageForStateOrProvinceCode
          }
          locationsErrorMessageForPostalCode={
            props.locationsErrorMessageForPostalCode
          }
          errorMessageForCountry={props.errorMessageForCountry}
        />
      )}
    </Stack>
  );
};
export default CreditCardsCardAddress;
